import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { privacyApi, ExportJob } from '../../../services/privacyApi';
import { logger } from '../../../utils/logger';
import { DownloadIcon } from './Icons';

export default function DataPortabilityCard() {
    const { t } = useTranslation();
    const [jobId, setJobId] = useState<string | null>(localStorage.getItem('last_privacy_export_job'));
    const [job, setJob] = useState<ExportJob | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!jobId) return;

        let active = true;
        const poll = async () => {
            try {
                const updatedJob = await privacyApi.getExportStatus(jobId);
                if (!active) return;
                setJob(updatedJob);

                if (updatedJob.status === 'queued') {
                    setTimeout(poll, 3000);
                }
            } catch (err) {
                logger.error('Export job poll failed', err);
                if (active) {
                    setJobId(null);
                    localStorage.removeItem('last_privacy_export_job');
                }
            }
        };

        poll();
        return () => { active = false; };
    }, [jobId]);

    const handleStartExport = async () => {
        setLoading(true);
        try {
            const { jobId: newJobId } = await privacyApi.startExport();
            setJobId(newJobId);
            setJob({ id: newJobId, status: 'queued', createdAt: new Date().toISOString() });
            localStorage.setItem('last_privacy_export_job', newJobId);
        } catch (err) {
            logger.error('Failed to start export', err);
            alert(t('privacy.failed_export'));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setJobId(null);
        setJob(null);
        localStorage.removeItem('last_privacy_export_job');
    };

    return (
        <div className="privacy-card">
            <div className="card-header">
                <div className="header-icon download">
                    <DownloadIcon size={20} />
                </div>
                <h3>{t('privacy.data_portability')}</h3>
            </div>
            <p className="card-subtitle">{t('privacy.download_copy')}</p>

            <div className="export-status-area">
                {!jobId ? (
                    <button className="btn-primary-small" onClick={handleStartExport} disabled={loading}>
                        {loading ? t('privacy.starting') : t('privacy.request_export')}
                    </button>
                ) : (
                    <div className="job-status-container">
                        {job?.status === 'queued' && (
                            <div className="job-queued">
                                <div className="spinner-small"></div>
                                <span>{t('privacy.generating_archive')}</span>
                            </div>
                        )}

                        {job?.status === 'ready' && (
                            <div className="job-ready">
                                <div className="ready-meta">
                                    <strong>{t('privacy.export_ready')}</strong>
                                    <span>{t('privacy.expires_24')}</span>
                                </div>
                                <a
                                    href={privacyApi.getDownloadUrl(jobId, (job as any).downloadToken || '')}
                                    className="btn-download-export"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('privacy.download_json', { size: (job.sizeBytes! / 1024).toFixed(1) })}
                                </a>
                                <button className="btn-link-small" onClick={handleReset}>{t('privacy.new_request')}</button>
                            </div>
                        )}

                        {job?.status === 'failed' && (
                            <div className="job-failed">
                                <span className="error-text">{t('privacy.generation_failed')}</span>
                                <button className="btn-link-small" onClick={handleReset}>{t('chat.try_again')}</button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
